/**
 * Paperclip Chat demo — a scripted, self-contained simulation of the chat UI
 * running the same GLP-1 landscape prompt as the terminal demo (DemoPage.tsx).
 *
 * No backend: the "agent" is a timed script (see `runScript`). Real chat
 * components are reused wherever they are context-free (HomeHero, ToolCallItem,
 * ResponseSkeleton, ActionButton, ThemeToggle, Badge, the markdown/citation
 * renderer, SlideablePanel); the rest (rail, prompt box, user turn, citation viewer) are
 * lightweight replicas of the real components' markup. When the real chat's
 * markup changes, update the replicas here: DemoRail ↔ WorkspaceSidebar,
 * DemoPromptBox ↔ PromptInput, the user turn ↔ UserMessage, the citation
 * panel ↔ CitationSidebar + DocumentViewerPage.
 *
 * Routed at /demo/chat. Click anywhere to skip to the end.
 */
import { NavBar } from "@/components/layout/NavBar";
import { SkipToMainLink } from "@/components/layout/SkipToMainLink";
import { Badge } from "@/components/ui/Badge";
import { SlideablePanel } from "@/components/ui/SlideablePanel";
import { ActionButton } from "@/features/chat/components/actions/ActionButton";
import {
  ChatMarkdownProvider,
  chatMarkdownComponents,
} from "@/features/chat/components/chatMarkdownComponents";
import { ResponseSkeleton } from "@/features/chat/components/StatusIndicators";
import { ToolCallItem } from "@/features/chat/components/ToolCallItem";
import type { MapProgress } from "@/features/chat/hooks/useMapProgress";
import type { TraceItem, TraceToolCall } from "@/features/chat/types";
import { HomeHero } from "@/features/home/components/HomeHero";
import { ThemeToggle } from "@/features/theme/ThemeToggle";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { cn } from "@/utils/cn";
import {
  ArrowUp,
  BookDown,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Copy,
  ExternalLink,
  FileDown,
  GitFork,
  Paperclip,
  Square,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Markdown from "react-markdown";
import { Link } from "react-router-dom";
import remarkGfm from "remark-gfm";
import "../styles/demo-chat.css";

// ─────────────────────────────────────────────────────────────────────────────
// Script content — same prompt and findings as the terminal demo.
// ─────────────────────────────────────────────────────────────────────────────

const PROMPT =
  "Build a comprehensive landscape of GLP-1 receptor agonist clinical trials across ALL indications. For every drug, find every Phase 2/3 trial — diabetes, obesity, NASH, heart failure, CKD, Alzheimer's, sleep apnea, and more. Extract trial name, phase, N, primary endpoint, result, and FDA decision with line-level citations.";

const SESSION_TITLE = "Build a comprehensive landscape of GLP-1 receptor agonist clinical trials";

const INTRO_TEXT =
  "I'll search across FDA regulatory reviews, clinical trial registries, and published papers to build a comprehensive landscape. Let me start with paperclip.";

const MIDPOINT_TEXT =
  "I have the regulatory reviews, registry entries, and published results across all three corpora. Now I'll extract structured trial data from every FDA document and verify the primary endpoints line by line.";

interface ToolDef {
  id: string;
  command: string;
  output: string;
}

const TOOL_FDA: ToolDef = {
  id: "tc-fda",
  command: `paperclip search -s fda "GLP-1 receptor agonist NDA approval" -n 20`,
  output: `Found 20 documents

 1. Ozempic — NDA209637 · semaglutide · SUSTAIN trials
 2. Wegovy — NDA215256 · semaglutide 2.4mg · STEP trials
 3. Mounjaro — NDA215866 · tirzepatide · SURPASS trials
 4. Zepbound — NDA217806 · tirzepatide · SURMOUNT trials
 5. Trulicity — BLA125469 · dulaglutide · AWARD / REWIND
 6. Victoza — NDA022341 · liraglutide · LEAD / LEADER
    ... 14 more FDA review docs`,
};

const TOOL_TRIALS: ToolDef = {
  id: "tc-trials",
  command: `paperclip search -s trials "semaglutide tirzepatide phase 3" -n 20`,
  output: `Found 20 documents

 1. NCT03548935 · SELECT · CV outcomes · N=17,604
 2. NCT04625985 · FLOW · CKD · N=3,533
 3. NCT04788511 · STEP-HFpEF · Heart failure · N=529
 4. NCT04184622 · SURMOUNT-1 · Obesity · N=2,539
 5. NCT05412004 · SURMOUNT-OSA · Sleep apnea · N=469
    ... searching tirzepatide, dulaglutide, liraglutide...`,
};

const TOOL_PMC: ToolDef = {
  id: "tc-pmc",
  command: `paperclip search -s pmc "SUSTAIN STEP SURPASS SELECT FLOW phase 3 results" -n 15`,
  output: `Found 15 papers

 1. STEP 1 — semaglutide 2.4mg: -14.9% body weight (PMC7928659)
 2. SELECT — 20% CV risk reduction, HR 0.80 (PMC11271387)
 3. FLOW — 24% kidney risk reduction, HR 0.76 (PMC11282385)
 4. SURPASS-2 — tirzepatide vs semaglutide, -2.0% HbA1c (PMC8351247)
    ... 11 more`,
};

const TOOL_MAP: ToolDef = {
  id: "tc-map",
  command: `paperclip map --from s_87b3b489 "Extract trial name, N, primary endpoint, comparator, result, approval status"`,
  output: `Map complete: 20/20 tasks
✓ Extracted 68 trial entries across 13 drugs
  → results saved to /clipboard/maps/m_4e2a19c7`,
};

const TOOL_GREP: ToolDef = {
  id: "tc-grep",
  command: `paperclip grep "primary endpoint" /fda/fda_bed63670b617/content.lines`,
  output: `L351: SUSTAIN 1 — 388 patients, HbA1c at 30 wk
L361: SUSTAIN 2 — 1,231 patients vs sitagliptin

2 total hits`,
};

/** Documents behind each [N] citation in the final answer. */
interface DemoCitation {
  id: number;
  docId: string;
  source: "fda" | "papers" | "trials";
  badge: string;
  sourceLabel: string;
  title: string;
  subtitle: string;
  chip?: string;
  line: number;
  totalLines: number;
  before: string;
  snippet: string;
  after: string;
}

const CITATIONS: DemoCitation[] = [
  {
    id: 1,
    docId: "fda_bed63670b617",
    source: "fda",
    badge: "FDA",
    sourceLabel: "FDA",
    title: "Ozempic (semaglutide) injection — Clinical Review",
    subtitle: "NDA 209637 · Center for Drug Evaluation and Research · December 2017",
    chip: "semaglutide",
    line: 351,
    totalLines: 1554,
    before:
      "The primary efficacy endpoint in each of the SUSTAIN trials was the change from baseline in HbA1c at the end of the treatment period.",
    snippet:
      "SUSTAIN 1 (NN9535-3623) randomized 388 patients with type 2 diabetes inadequately controlled on diet and exercise to semaglutide 0.5 mg, 1.0 mg, or placebo for 30 weeks. The primary endpoint was change in HbA1c from baseline to week 30; semaglutide reduced HbA1c by 1.5% (0.5 mg) and 1.6% (1.0 mg) versus 0.0% for placebo (p<0.0001).",
    after:
      "Secondary endpoints included change in body weight, fasting plasma glucose, and the proportion of patients achieving HbA1c <7.0%.",
  },
  {
    id: 2,
    docId: "PMC7928659",
    source: "papers",
    badge: "PMC",
    sourceLabel: "PubMed Central",
    title: "Once-Weekly Semaglutide in Adults with Overweight or Obesity (STEP 1)",
    subtitle: "Wilding, J. P. H. et al. · N Engl J Med (2021)",
    line: 31,
    totalLines: 412,
    before:
      "We enrolled 1,961 adults with a body-mass index of 30 or greater (≥27 with at least one weight-related coexisting condition) who did not have diabetes.",
    snippet:
      "The mean change in body weight from baseline to week 68 was −14.9% in the semaglutide group as compared with −2.4% with placebo, for an estimated treatment difference of −12.4 percentage points (95% CI, −13.4 to −11.5; P<0.001).",
    after:
      "More participants in the semaglutide group than in the placebo group achieved weight reductions of 5% or more (86.4% vs. 31.5%).",
  },
  {
    id: 3,
    docId: "PMC11271387",
    source: "papers",
    badge: "PMC",
    sourceLabel: "PubMed Central",
    title: "Semaglutide and Cardiovascular Outcomes in Obesity without Diabetes (SELECT)",
    subtitle: "Lincoff, A. M. et al. · N Engl J Med (2023)",
    line: 45,
    totalLines: 388,
    before:
      "A total of 17,604 patients were enrolled; 8,803 were assigned to receive semaglutide and 8,801 to receive placebo. The mean duration of exposure was 34.2 months.",
    snippet:
      "A primary cardiovascular end-point event occurred in 569 of the 8,803 patients (6.5%) in the semaglutide group and in 701 of the 8,801 patients (8.0%) in the placebo group (hazard ratio, 0.80; 95% CI, 0.72 to 0.90; P<0.001 for superiority).",
    after:
      "Adverse events leading to permanent discontinuation occurred in 16.6% of the semaglutide group and 8.2% of the placebo group.",
  },
  {
    id: 4,
    docId: "PMC11282385",
    source: "papers",
    badge: "PMC",
    sourceLabel: "PubMed Central",
    title: "Effects of Semaglutide on Chronic Kidney Disease in Patients with Type 2 Diabetes (FLOW)",
    subtitle: "Perkovic, V. et al. · N Engl J Med (2024)",
    line: 33,
    totalLines: 402,
    before:
      "We randomly assigned 3,533 participants with type 2 diabetes and chronic kidney disease to receive semaglutide 1.0 mg weekly or placebo.",
    snippet:
      "The risk of a primary-outcome event was 24% lower in the semaglutide group than in the placebo group (331 vs. 410 first events; hazard ratio, 0.76; 95% CI, 0.66 to 0.88; P=0.0003).",
    after:
      "Results were similar for a composite of the kidney-specific components of the primary outcome (hazard ratio, 0.79) and for death from cardiovascular causes (hazard ratio, 0.71).",
  },
  {
    id: 5,
    docId: "NCT04788511",
    source: "trials",
    badge: "TRIAL",
    sourceLabel: "ClinicalTrials.gov",
    title: "STEP-HFpEF: Semaglutide in Obesity-Related Heart Failure with Preserved Ejection Fraction",
    subtitle: "NCT04788511 · Phase 3 · Novo Nordisk · Completed",
    line: 88,
    totalLines: 240,
    before: "Enrollment: 529 participants. Allocation: randomized, double-blind, placebo-controlled.",
    snippet:
      "Primary outcome: change from baseline to week 52 in KCCQ-CSS was +16.6 points with semaglutide vs +8.7 with placebo (difference 7.8 points); change in body weight was −13.3% vs −2.6%.",
    after: "Secondary outcomes: 6-minute walk distance, hierarchical composite endpoint, and C-reactive protein.",
  },
  {
    id: 6,
    docId: "fda_2f1c9a7e4b21",
    source: "fda",
    badge: "FDA",
    sourceLabel: "FDA",
    title: "Mounjaro (tirzepatide) injection — Clinical Review",
    subtitle: "NDA 215866 · Center for Drug Evaluation and Research · May 2022",
    chip: "tirzepatide",
    line: 76,
    totalLines: 1298,
    before:
      "SURPASS-2 was an open-label, active-controlled trial comparing three doses of tirzepatide with semaglutide 1 mg in 1,879 patients on metformin.",
    snippet:
      "At week 40, the mean change in HbA1c from baseline was −2.01%, −2.24%, and −2.30% with tirzepatide 5, 10, and 15 mg, respectively, versus −1.86% with semaglutide; all three doses met non-inferiority and superiority.",
    after: "Body weight reductions were 7.6 kg, 9.3 kg, and 11.2 kg versus 5.7 kg for semaglutide.",
  },
  {
    id: 7,
    docId: "fda_9a41d0c3e7f5",
    source: "fda",
    badge: "FDA",
    sourceLabel: "FDA",
    title: "Zepbound (tirzepatide) injection — Clinical Review",
    subtitle: "NDA 217806 · Center for Drug Evaluation and Research · November 2023",
    chip: "tirzepatide",
    line: 142,
    totalLines: 1187,
    before:
      "SURMOUNT-1 randomized 2,539 adults with obesity (or overweight with at least one comorbidity) without diabetes to tirzepatide 5, 10, or 15 mg or placebo for 72 weeks.",
    snippet:
      "The mean percent change in body weight at week 72 was −15.0%, −19.5%, and −20.9% with tirzepatide 5, 10, and 15 mg, respectively, versus −3.1% with placebo (p<0.001 for all).",
    after: "The percentage of participants achieving ≥5% weight reduction was 85%, 89%, and 91% versus 35% with placebo.",
  },
  {
    id: 8,
    docId: "NCT01179048",
    source: "trials",
    badge: "TRIAL",
    sourceLabel: "ClinicalTrials.gov",
    title: "LEADER: Liraglutide Effect and Action in Diabetes — Evaluation of Cardiovascular Outcome Results",
    subtitle: "NCT01179048 · Phase 3 · Novo Nordisk · Completed",
    line: 177,
    totalLines: 310,
    before: "Enrollment: 9,340 participants with type 2 diabetes and high cardiovascular risk; median follow-up 3.8 years.",
    snippet:
      "Primary outcome: the composite of cardiovascular death, non-fatal MI, or non-fatal stroke occurred in 13.0% of liraglutide vs 14.9% of placebo participants (HR 0.87; 95% CI 0.78–0.97; P=0.01 for superiority).",
    after: "Secondary outcomes: all-cause mortality (HR 0.85) and expanded composite cardiovascular outcome.",
  },
  {
    id: 9,
    docId: "fda_c71e5b2d8a90",
    source: "fda",
    badge: "FDA",
    sourceLabel: "FDA",
    title: "Trulicity (dulaglutide) injection — Clinical Review, REWIND supplement",
    subtitle: "BLA 125469/S-036 · Center for Drug Evaluation and Research · February 2020",
    chip: "dulaglutide",
    line: 364,
    totalLines: 902,
    before: "REWIND enrolled 9,901 patients with type 2 diabetes, of whom 31% had established cardiovascular disease; median follow-up was 5.4 years.",
    snippet:
      "The primary composite outcome (cardiovascular death, non-fatal MI, or non-fatal stroke) occurred in 12.0% of dulaglutide vs 13.4% of placebo participants (HR 0.88; 95% CI 0.79–0.99; p=0.026).",
    after: "The effect was consistent in patients with and without prior cardiovascular disease.",
  },
  {
    id: 10,
    docId: "NCT01144338",
    source: "trials",
    badge: "TRIAL",
    sourceLabel: "ClinicalTrials.gov",
    title: "EXSCEL: Exenatide Study of Cardiovascular Event Lowering",
    subtitle: "NCT01144338 · Phase 3 · AstraZeneca · Completed",
    line: 213,
    totalLines: 276,
    before: "Enrollment: 14,752 participants with type 2 diabetes, 73% with prior cardiovascular events; median follow-up 3.2 years.",
    snippet:
      "Primary outcome: MACE occurred in 11.4% of exenatide vs 12.2% of placebo participants (HR 0.91; 95% CI 0.83–1.00; P<0.001 for non-inferiority, P=0.06 for superiority).",
    after: "Exenatide once weekly was non-inferior to placebo for cardiovascular safety but did not demonstrate superiority.",
  },
];

const citationUrl = (c: DemoCitation) =>
  `https://citations.gxl.ai/${c.source}/${c.docId}#L${c.line}`;

const FINAL_RESPONSE = `## GLP-1 receptor agonist landscape

Across **18 drugs × 12 indications**, I found **135 Phase 2/3 trials** and verified **100 line-level citations** from FDA reviews, ClinicalTrials.gov, and PubMed Central.

### Key findings

- **Semaglutide has the broadest evidence base.** It is approved or in Phase 3 for T2D (SUSTAIN [1]), obesity (STEP 1: −14.9% body weight [2]), cardiovascular outcomes (SELECT: HR 0.80 [3]), CKD (FLOW: HR 0.76 [4]), and obesity-related HFpEF (STEP-HFpEF [5]).
- **Tirzepatide produces the largest weight loss to date.** SURMOUNT-1 reported −20.9% at 72 weeks [7], and in T2D it beat semaglutide 1 mg head-to-head on HbA1c in SURPASS-2 [6].
- **Cardiovascular benefit is class-wide but not uniform.** LEADER (liraglutide, HR 0.87 [8]) and REWIND (dulaglutide, HR 0.88 [9]) were positive, while EXSCEL (exenatide, HR 0.91) met non-inferiority only [10].

### Cross-database table

| Drug | Indication | Trial | N | Result | Source |
|---|---|---|---|---|---|
| Semaglutide | T2D | SUSTAIN 1 | 388 | −1.5% HbA1c | [1] |
| Semaglutide | Obesity | STEP 1 | 1,961 | −14.9% body wt | [2] |
| Semaglutide | CV outcomes | SELECT | 17,604 | HR 0.80 (0.72–0.90) | [3] |
| Semaglutide | CKD | FLOW | 3,533 | HR 0.76 (0.66–0.88) | [4] |
| Semaglutide | Heart failure | STEP-HFpEF | 529 | +7.8 KCCQ pts vs placebo | [5] |
| Tirzepatide | T2D | SURPASS-2 | 1,879 | −2.0% HbA1c | [6] |
| Tirzepatide | Obesity | SURMOUNT-1 | 2,539 | −20.9% body wt | [7] |
| Liraglutide | CV outcomes | LEADER | 9,340 | HR 0.87 (0.78–0.97) | [8] |
| Dulaglutide | CV outcomes | REWIND | 9,901 | HR 0.88 (0.79–0.99) | [9] |
| Exenatide | CV outcomes | EXSCEL | 14,752 | HR 0.91 (neutral) | [10] |

*… 125 more rows across 18 drugs × 12 indications. I saved the complete landscape to your clipboard as* \`glp1-landscape.html\`.

### References

${CITATIONS.map(
  (c) =>
    `[${c.id}] ${c.title}. *${c.subtitle.split(" · ")[0]}*. ${citationUrl(c)}`,
).join("\n\n")}
`;

// ─────────────────────────────────────────────────────────────────────────────
// Rail (replica of WorkspaceSidebar on the chat home — no auth / API calls)
// ─────────────────────────────────────────────────────────────────────────────

const RAIL_ITEMS = [
  { label: "Home", active: true },
  { label: "Clipboard" },
  { label: "Playground" },
  { label: "Pricing" },
  { label: "Docs" },
];

const PRIOR_SESSIONS = [
  "Is coffee or matcha better for your health? Summarize the evidence",
  "Summarize the literature on CAR-T in multiple myeloma",
  "Compare JAK inhibitor safety signals across FDA labels",
];

const DemoRail = ({
  currentSession,
  isStreaming,
}: {
  currentSession: string | null;
  isStreaming: boolean;
}) => (
  <aside
    className="hidden h-full w-[240px] shrink-0 flex-col bg-white lg:flex"
    aria-label="Workspace navigation"
  >
    <nav className="shrink-0 pt-5" aria-label="Primary">
      {RAIL_ITEMS.map(({ label, active }) => (
        <div
          key={label}
          aria-current={active ? "page" : undefined}
          className={cn(
            "font-ui flex w-full items-center px-5 py-[2px] text-left leading-5 transition-colors",
            active ? "font-medium text-zinc-900" : "text-zinc-400 hover:text-zinc-800",
          )}
        >
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <span className="truncate">{label}</span>
          </span>
        </div>
      ))}
    </nav>

    <div className="mt-4 flex min-h-0 flex-1 flex-col border-t border-zinc-200/70 pt-3">
      <div className="font-ui flex shrink-0 items-baseline justify-between px-5 pb-2 text-zinc-400">
        <span>Sessions</span>
        <span className="font-ui text-zinc-400">New</span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {[
          ...(currentSession ? [{ title: currentSession, active: true }] : []),
          ...PRIOR_SESSIONS.map((title) => ({ title, active: false })),
        ].map(({ title, active }) => (
          <div
            key={title}
            className={cn(
              "flex w-full items-center px-5 py-1 text-left text-xs leading-6 transition-colors fi",
              active ? "text-zinc-900" : "text-zinc-400",
            )}
          >
            <span
              className={cn(
                "font-serif flex-1 overflow-hidden whitespace-nowrap pr-6",
                active && isStreaming && "animate-pulse-fast",
              )}
              style={{
                maskImage: "linear-gradient(to right, black 75%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(to right, black 75%, transparent 100%)",
              }}
            >
              {title}
            </span>
          </div>
        ))}
      </div>
    </div>

    <div className="shrink-0 space-y-3 px-5 py-4">
      <ThemeToggle />
      <div className="flex w-full items-center gap-2.5 py-1 text-left">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-[10px] font-medium text-white">
          GX
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-zinc-800">GXL Research</span>
          <span className="block truncate text-2xs text-zinc-400">research@gxl.ai</span>
        </span>
        <ChevronsUpDown className="h-3 w-3 shrink-0 text-zinc-300" />
      </div>
    </div>
  </aside>
);

// ─────────────────────────────────────────────────────────────────────────────
// Prompt box (replica of PromptInput's editorial markup)
// ─────────────────────────────────────────────────────────────────────────────

/** Same list and cadence as PromptInput's SampleTicker on the home state. */
const SAMPLE_PROMPTS = [
  "What is the IC50 of MRTX1133 against KRAS G12D?",
  "Which FDA labels warn about QTc prolongation?",
  "Compare sotorasib and adagrasib in NSCLC trials",
  "Which bioRxiv preprints on GLP-1 agonists were later published?",
  "Summarize the eligibility criteria of NCT04280705",
];
const SAMPLE_DWELL_MS = 3600;
const SAMPLE_ROLL_MS = 420;

const DemoSampleTicker = () => {
  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState<number | null>(null);
  useEffect(() => {
    const tick = window.setInterval(() => {
      setIndex((i) => {
        setLeaving(i);
        return (i + 1) % SAMPLE_PROMPTS.length;
      });
    }, SAMPLE_DWELL_MS);
    return () => window.clearInterval(tick);
  }, []);
  useEffect(() => {
    if (leaving === null) return;
    const t = window.setTimeout(() => setLeaving(null), SAMPLE_ROLL_MS);
    return () => window.clearTimeout(t);
  }, [leaving]);
  return (
    <div className="chat-samples" aria-label="Sample questions">
      <span>Try</span>
      <div className="chat-samples-window" aria-live="polite">
        {leaving !== null && (
          <span key={`out-${leaving}`} className="chat-sample chat-sample--out" aria-hidden>
            {SAMPLE_PROMPTS[leaving]}
          </span>
        )}
        <span
          key={`in-${index}`}
          className={cn("chat-sample", leaving !== null && "chat-sample--in")}
        >
          {SAMPLE_PROMPTS[index]}
        </span>
      </div>
    </div>
  );
};

const DemoPromptBox = ({
  text,
  typing,
  streaming,
  isHome,
}: {
  text: string;
  typing: boolean;
  streaming: boolean;
  isHome: boolean;
}) => (
  <div className="prompt-box-container shrink-0 bg-transparent">
    <div
      className={cn(
        "prompt-box-items relative w-full max-w-[58rem] px-6 -translate-y-3 space-y-2",
        isHome ? "chat-home-col" : "mx-auto",
      )}
    >
      {isHome && <DemoSampleTicker />}
      <div
        className={cn(
          "prompt-box prompt-box--editorial flex flex-col bg-transparent",
          typing && "is-focused",
        )}
      >
        <div className="flex items-end min-w-0">
          <div className="flex-1 min-w-0 overflow-hidden">
            <div
              className="prompt-editorial text-base leading-7 text-zinc-900 whitespace-pre-wrap break-words transition-[min-height] duration-200 ease-linear"
              style={{ minHeight: typing ? 56 : 28 }}
            >
              {text ? (
                <>
                  {text}
                  {typing && <span className="demo-caret" aria-hidden />}
                </>
              ) : (
                <span className="italic text-zinc-400">
                  Ask a question...
                  {typing && <span className="demo-caret not-italic" aria-hidden />}
                </span>
              )}
            </div>
          </div>
          <div className="font-ui flex items-center gap-5 shrink-0 pl-4 pb-[7px] text-sm leading-none">
            <span className="inline-flex items-center gap-1.5 text-zinc-400">
              <Paperclip className="h-3.5 w-3.5" aria-hidden />
              Attach
            </span>
            {streaming ? (
              <span className="inline-flex items-center gap-1.5 text-zinc-900">
                <Square className="h-3 w-3" aria-hidden />
                Stop
              </span>
            ) : (
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 transition-colors",
                  text ? "text-zinc-900" : "text-zinc-300",
                )}
              >
                Send
                <ArrowUp className="h-3.5 w-3.5" aria-hidden />
              </span>
            )}
          </div>
        </div>
      </div>
      <p className="mt-2 flex justify-between px-1 text-sm leading-5 text-zinc-400">
        <span>Enter to send · Shift+Enter for a new line</span>
        <a href="mailto:support@gxl.ai" className="font-ui hover:text-zinc-700">
          Questions or feedback?
        </a>
      </p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Citation panel (replica of CitationSidebar + DocumentViewerPage, static doc)
// ─────────────────────────────────────────────────────────────────────────────

const ContextDivider = ({ count }: { count: number }) => (
  <div className="my-1">
    <div className="w-full flex items-center gap-3 py-2.5 text-xs text-zinc-400">
      <span className="flex-1 border-t border-dashed border-zinc-200" />
      <span className="shrink-0 flex items-center gap-1.5 select-none">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-50">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Show {count.toLocaleString()} lines
      </span>
      <span className="flex-1 border-t border-dashed border-zinc-200" />
    </div>
  </div>
);

const DemoCitationPanel = ({
  citation,
  onClose,
  onPrev,
  onNext,
}: {
  citation: DemoCitation;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}) => {
  const highlight =
    "bg-amber-50 border-l-[3px] border-l-amber-400 pl-4 -ml-4 rounded-r";
  return (
    <div className="flex h-full w-full flex-col bg-white">
      {/* Header — mirrors CitationSidebar */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-3xs text-gray-400 font-mono flex-shrink-0">
            [{citation.id}]
          </span>
          <span className="text-xs text-gray-600 truncate">{citation.title}</span>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            type="button"
            onClick={onPrev}
            disabled={!onPrev}
            aria-label="Previous citation"
            className="h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:bg-gray-100 disabled:opacity-30"
          >
            <ChevronUp className="w-3.5 h-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!onNext}
            aria-label="Next citation"
            className="h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:bg-gray-100 disabled:opacity-30"
          >
            <ChevronDown className="w-3.5 h-3.5" aria-hidden />
          </button>
          <span className="h-6 w-6 flex items-center justify-center rounded text-gray-400">
            <ExternalLink className="w-3.5 h-3.5" aria-hidden />
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close citation panel"
            className="h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:bg-gray-100"
          >
            <X className="w-4 h-4" aria-hidden />
          </button>
        </div>
      </div>

      {/* Document viewer — mirrors DocumentViewerPage */}
      <div className="flex-1 overflow-y-auto bg-white">
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-zinc-200">
          <div className="max-w-3xl mx-auto px-6 py-2.5 flex items-center justify-between">
            <span className="text-sm font-semibold text-zinc-900">gxl</span>
            <div className="flex items-center gap-3 text-xs text-zinc-400">
              <Badge variant="default" className="text-2xs pt-1 pb-0.25">{citation.badge}</Badge>
              <span className="font-mono">{citation.docId}</span>
            </div>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-6 pt-8 pb-10">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 mb-4">
            View on {citation.sourceLabel}
          </span>
          {citation.chip && (
            <p className="text-xs font-medium text-blue-700 bg-blue-50 rounded px-2 py-1 mb-3 inline-block">
              {citation.chip}
            </p>
          )}
          <h1 className="font-display text-4xl font-normal text-zinc-900 leading-[1.15] mb-2 tracking-tight">
            {citation.title}
          </h1>
          <p className="text-sm text-zinc-500 mb-6">{citation.subtitle}</p>

          <ContextDivider count={citation.line - 2} />
          <div className="text-base text-zinc-800 leading-[1.7] mb-3">
            {citation.before}
          </div>
          <div className="relative">
            <span className="absolute -left-20 top-[2px] select-none text-xs font-mono font-medium text-amber-500 whitespace-nowrap">
              Line {citation.line}:
            </span>
            <div
              id={`L${citation.line}`}
              className={cn("text-base text-zinc-800 leading-[1.7] mb-3", highlight)}
            >
              {citation.snippet}
            </div>
          </div>
          <div className="text-base text-zinc-800 leading-[1.7] mb-3">
            {citation.after}
          </div>
          <ContextDivider count={citation.totalLines - citation.line - 1} />
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

type Phase = "home" | "chat";

const makeTool = (
  def: ToolDef,
  status: TraceToolCall["status"],
): TraceToolCall => ({
  id: def.id,
  name: "papers_bash",
  status,
  input: { command: def.command },
  ...(status === "complete" ? { output: def.output } : {}),
});

const FINAL_ITEMS: TraceItem[] = [
  { type: "text", content: INTRO_TEXT },
  { type: "tool_call", toolCall: makeTool(TOOL_FDA, "complete") },
  { type: "tool_call", toolCall: makeTool(TOOL_TRIALS, "complete") },
  { type: "tool_call", toolCall: makeTool(TOOL_PMC, "complete") },
  { type: "text", content: MIDPOINT_TEXT },
  { type: "tool_call", toolCall: makeTool(TOOL_MAP, "complete") },
  { type: "tool_call", toolCall: makeTool(TOOL_GREP, "complete") },
];

/** The home masthead, exactly as MessageList renders it in the chat's home state. */
const HomeMasthead = () => (
  <div className="editorial-frame editorial-frame-home chat-home-col flex flex-col">
    <HomeHero />
  </div>
);

export const ChatDemoPage = () => {
  useDocumentTitle("Demo · Chat");

  const [phase, setPhase] = useState<Phase>("home");
  const [heroLeaving, setHeroLeaving] = useState(false);
  const [inputText, setInputText] = useState("");
  const [typing, setTyping] = useState(false);
  const [userMessage, setUserMessage] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [items, setItems] = useState<TraceItem[]>([]);
  const [mapProgress, setMapProgress] = useState<MapProgress | null>(null);
  const [finalResponse, setFinalResponse] = useState("");
  const [done, setDone] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState<number | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(512);
  const [cursor, setCursor] = useState<{ x: number; y: number; show: boolean }>({
    x: 0,
    y: 0,
    show: false,
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);
  const skippedRef = useRef(false);

  // ── Citations ──
  const resolvedIds = useMemo(() => new Set(CITATIONS.map((c) => c.id)), []);
  const citationsByDocId = useMemo(
    () => new Map(CITATIONS.map((c) => [c.docId, c.id])),
    [],
  );
  const openCitation = useCallback((id: number) => {
    setSelectedCitation(id);
    setPanelOpen(true);
  }, []);
  const closePanel = useCallback(() => setPanelOpen(false), []);
  const selected = CITATIONS.find((c) => c.id === selectedCitation) ?? null;
  const selectedIdx = selected ? CITATIONS.indexOf(selected) : -1;

  // ── Auto-scroll while the answer streams ──
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items, finalResponse, userMessage, mapProgress]);

  // The masthead lifts away for 480ms while the conversation rolls in — same
  // choreography as MessageList on the first send.
  const startChat = useCallback(() => {
    setPhase("chat");
    setHeroLeaving(true);
    window.setTimeout(() => setHeroLeaving(false), 480);
  }, []);

  // ── Skip to end (any click that isn't on an interactive demo element) ──
  const skipToEnd = useCallback(() => {
    if (skippedRef.current) return;
    skippedRef.current = true;
    cancelledRef.current = true;
    setTyping(false);
    setInputText("");
    setPhase("chat");
    setHeroLeaving(false);
    setUserMessage(PROMPT);
    setMapProgress(null);
    setItems(FINAL_ITEMS);
    setFinalResponse(FINAL_RESPONSE);
    setIsStreaming(false);
    setDone(true);
    setCursor((c) => ({ ...c, show: false }));
    setSelectedCitation(1);
    setPanelOpen(true);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest(".cta, button, a, aside, .slideable-panel")) return;
      skipToEnd();
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [skipToEnd]);

  // ── The script ──
  useEffect(() => {
    // `alive` is per effect run so StrictMode's dev double-mount can't leave
    // two scripts racing; `cancelledRef` is the user-facing skip-to-end.
    let alive = true;
    cancelledRef.current = false;

    const sleep = (ms: number) =>
      new Promise<void>((resolve, reject) =>
        setTimeout(
          () =>
            !alive || cancelledRef.current
              ? reject(new Error("cancelled"))
              : resolve(),
          ms,
        ),
      );

    const addItem = (item: TraceItem) => setItems((prev) => [...prev, item]);
    const setToolStatus = (def: ToolDef, status: TraceToolCall["status"]) =>
      setItems((prev) =>
        prev.map((it) =>
          it.type === "tool_call" && it.toolCall.id === def.id
            ? { type: "tool_call", toolCall: makeTool(def, status) }
            : it,
        ),
      );
    const runTool = async (def: ToolDef, ms: number) => {
      addItem({ type: "tool_call", toolCall: makeTool(def, "running") });
      await sleep(ms);
      setToolStatus(def, "complete");
    };

    const runScript = async () => {
      // 1. Type the prompt into the box, then "send" it.
      await sleep(1200);
      setTyping(true);
      for (let i = 1; i <= PROMPT.length; i++) {
        setInputText(PROMPT.slice(0, i));
        await sleep(9 + Math.random() * 6);
      }
      await sleep(500);
      setTyping(false);
      setInputText("");
      setUserMessage(PROMPT);
      setIsStreaming(true);
      startChat();

      // 2. Agent responds (skeleton while the first tokens are "in flight"),
      //    then searches the three corpora.
      await sleep(1100);
      addItem({ type: "text", content: INTRO_TEXT });
      await sleep(1100);
      await runTool(TOOL_FDA, 1900);
      await sleep(600);
      await runTool(TOOL_TRIALS, 1700);
      await sleep(600);
      await runTool(TOOL_PMC, 1700);
      await sleep(700);

      // 3. Map across results (with live progress), then grep to verify.
      addItem({ type: "text", content: MIDPOINT_TEXT });
      await sleep(900);
      addItem({ type: "tool_call", toolCall: makeTool(TOOL_MAP, "running") });
      const started = Date.now() / 1000;
      setMapProgress({ total: 20, completed: 0, failed: 0, in_progress: 4, started_at: started });
      for (let c = 1; c <= 20; c++) {
        await sleep(140);
        setMapProgress({
          total: 20,
          completed: c,
          failed: 0,
          in_progress: Math.min(4, 20 - c),
          started_at: started,
        });
      }
      await sleep(300);
      setMapProgress(null);
      setToolStatus(TOOL_MAP, "complete");
      await sleep(600);
      await runTool(TOOL_GREP, 1500);
      await sleep(1200);

      // 4. Stream the final answer.
      const tokens = FINAL_RESPONSE.split(/(\s+)/);
      let acc = "";
      for (let i = 0; i < tokens.length; i += 4) {
        acc += tokens.slice(i, i + 4).join("");
        setFinalResponse(acc);
        await sleep(22);
      }
      setFinalResponse(FINAL_RESPONSE);
      setIsStreaming(false);
      setDone(true);

      // 5. "Click" citation [1] to open the line-level source.
      await sleep(1200);
      const btn = document.querySelector<HTMLElement>('button[aria-label="View citation 1"]');
      if (btn) {
        btn.scrollIntoView({ block: "center", behavior: "smooth" });
        await sleep(700);
        const r = btn.getBoundingClientRect();
        setCursor({ x: r.left + r.width / 2 + 60, y: r.top + r.height / 2 + 80, show: true });
        await sleep(50);
        setCursor({ x: r.left + r.width / 2, y: r.top + r.height / 2, show: true });
        await sleep(800);
      }
      openCitation(1);
      await sleep(600);
      setCursor((c) => ({ ...c, show: false }));
    };

    runScript().catch(() => {
      /* cancelled by skip-to-end or unmount */
    });

    return () => {
      alive = false;
      // Reset anything the aborted run may have started so a re-mount starts clean.
      setItems([]);
      setInputText("");
      setTyping(false);
      setMapProgress(null);
    };
  }, [openCitation, startChat]);

  // ── Render ──
  const isHome = phase === "home";
  const hasContent = items.length > 0;

  const renderItems = () => {
    const elements: React.ReactNode[] = [];
    let toolGroup: React.ReactNode[] = [];
    const flush = () => {
      if (toolGroup.length) {
        elements.push(
          <div key={`tg-${elements.length}`} className="my-1">
            {toolGroup}
          </div>,
        );
        toolGroup = [];
      }
    };
    items.forEach((item, i) => {
      if (item.type === "text") {
        flush();
        elements.push(
          <div key={`t-${i}`} className="fi">
            <Markdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
              {item.content}
            </Markdown>
          </div>,
        );
      } else {
        toolGroup.push(
          <div key={item.toolCall.id} className="fi">
            <ToolCallItem
              toolCall={item.toolCall}
              mapProgress={
                item.toolCall.id === TOOL_MAP.id && item.toolCall.status === "running"
                  ? mapProgress
                  : undefined
              }
            />
          </div>,
        );
      }
    });
    flush();
    return elements;
  };

  return (
    <div className="demo-chat layout flex h-dvh flex-col overflow-hidden bg-white">
      <SkipToMainLink />
      <NavBar showPrimaryLinks={false} />

      <div className="layout-items flex min-h-0 flex-1 overflow-hidden">
        <DemoRail
          currentSession={phase === "chat" ? SESSION_TITLE : null}
          isStreaming={isStreaming}
        />

        <main
          id="main-content"
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        >
          <div className="flex h-full min-h-0 flex-1 overflow-hidden">
            {/* Main chat column — mirrors ChatLayout: on the home state the
                masthead and the prompt are one centred composition; once a
                conversation exists the list fills and the prompt docks. */}
            <div
              className={cn(
                "flex min-h-0 min-w-0 flex-1 flex-col",
                isHome ? "overflow-y-auto overscroll-contain" : "overflow-hidden",
              )}
            >
              <div className={cn("shrink-0", isHome ? "flex-[2] min-h-4" : "hidden")} aria-hidden />

              {isHome ? (
                <div className="chat-home shrink-0">
                  <HomeMasthead />
                </div>
              ) : (
                <div
                  ref={scrollRef}
                  className="relative flex-1 min-h-0 overflow-y-auto overscroll-contain"
                  style={{ overflowAnchor: "auto" }}
                >
                  {heroLeaving && (
                    <div className="chat-home chat-home--leaving" aria-hidden>
                      <HomeMasthead />
                    </div>
                  )}
                  <div
                    className={cn(
                      "chat-thread mx-auto max-w-3xl px-4 py-6 space-y-6",
                      heroLeaving && "chat-rollout",
                    )}
                  >
                    {/* User turn — mirrors UserMessage */}
                    {userMessage && (
                      <div className="chat-user group flex items-start justify-end gap-2">
                        <div className="-mr-4 flex w-fit max-w-[75%] flex-col items-end gap-2.5 bg-zinc-900/[0.05] px-4 py-3.5 rounded-sm">
                          <p className="whitespace-pre-wrap text-right text-base leading-7 text-zinc-900">
                            {userMessage}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Assistant turn — mirrors AssistantMessage */}
                    <ChatMarkdownProvider
                      onCitationClick={openCitation}
                      resolvedIds={resolvedIds}
                      citationsByDocId={citationsByDocId}
                      fadeTokens={isStreaming}
                    >
                    <div
                      role="article"
                      aria-label="Assistant response"
                      aria-live="polite"
                      className="chat-prose text-base leading-7 text-zinc-900"
                    >
                      {isStreaming && !hasContent && (
                        <ResponseSkeleton lines={3} label="Responding" className="mb-3" />
                      )}

                      {renderItems()}

                      {finalResponse && (
                        <div className="mt-2">
                          <Markdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
                            {finalResponse}
                          </Markdown>
                        </div>
                      )}

                      {done && (
                        <>
                          {/* Actions — mirrors MessageActionsBar */}
                          <div className="mt-3 flex justify-start gap-1 fi">
                            <ActionButton label="Fork session" onClick={() => {}}>
                              <GitFork className="h-3.5 w-3.5" />
                            </ActionButton>
                            <ActionButton label="Copy message" onClick={() => {}}>
                              <Copy className="h-3.5 w-3.5" />
                            </ActionButton>
                            <ActionButton label="Download references" onClick={() => {}}>
                              <FileDown className="h-3.5 w-3.5" />
                            </ActionButton>
                            <ActionButton label="Save session" onClick={() => {}}>
                              <BookDown className="h-3.5 w-3.5" />
                            </ActionButton>
                            <ActionButton label="Good response" onClick={() => {}}>
                              <ThumbsUp className="h-3.5 w-3.5" />
                            </ActionButton>
                            <ActionButton label="Bad response" onClick={() => {}}>
                              <ThumbsDown className="h-3.5 w-3.5" />
                            </ActionButton>
                          </div>
                          <div className={cn("cta mt-6 pb-6", done && "show")}>
                            <Link to="/demo/glp1-landscape">View full report →</Link>
                          </div>
                        </>
                      )}
                    </div>
                    </ChatMarkdownProvider>
                  </div>
                </div>
              )}

              <DemoPromptBox text={inputText} typing={typing} streaming={isStreaming} isHome={isHome} />

              <div className={cn("shrink-0", isHome ? "flex-[3] min-h-6" : "hidden")} aria-hidden />
            </div>

            {/* Citation sidebar — same SlideablePanel the real chat uses */}
            <SlideablePanel
              isOpen={panelOpen}
              onClose={closePanel}
              offsetTop="3.5rem"
              from="right"
              width={`${panelWidth}px`}
              alwaysOpenOnDesktop={false}
              ariaLabel="Citation viewer"
              resizability={{ onWidthChange: setPanelWidth, minWidth: 320 }}
            >
              {selected && (
                <DemoCitationPanel
                  citation={selected}
                  onClose={closePanel}
                  onPrev={
                    selectedIdx > 0
                      ? () => setSelectedCitation(CITATIONS[selectedIdx - 1].id)
                      : undefined
                  }
                  onNext={
                    selectedIdx >= 0 && selectedIdx < CITATIONS.length - 1
                      ? () => setSelectedCitation(CITATIONS[selectedIdx + 1].id)
                      : undefined
                  }
                />
              )}
            </SlideablePanel>
          </div>
        </main>
      </div>

      {/* Simulated pointer for the citation click */}
      <svg
        className={cn("demo-cursor", cursor.show && "show")}
        style={{ left: cursor.x, top: cursor.y }}
        viewBox="0 0 16 16"
        aria-hidden
      >
        <path
          d="M2 1.5 L2 12.5 L5 9.8 L7.2 14.5 L9.4 13.6 L7.2 9 L11.2 9 Z"
          fill="#111"
          stroke="#fff"
          strokeWidth="1"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};
